'use client';

import * as React from 'react';
import Image from 'next/image';
import {
  AudioLines,
  BadgeCheck,
  Brain,
  CheckCircle,
  Clock,
  CreditCard,
  Edit,
  FileText,
  Focus,
  Gift,
  Globe,
  GraduationCap,
  Headphones,
  Languages,
  Lightbulb,
  LucideIcon,
  Megaphone,
  Mic,
  Mic2Icon,
  MicOff,
  RefreshCw,
  Rocket,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Target,
  Users,
  Video,
  Youtube,
  Zap
} from 'lucide-react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import { WhyChooseUsProps } from '~/types/why-choose-us-type';
import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';

import { trackCta } from '~/lib/track-cta';

const iconMap: Record<string, LucideIcon> = {
  mic: Mic,
  globe: Globe,
  languages: Languages,
  headphones: Headphones,
  edit: Edit,
  rocket: Rocket,
  brain: Brain,
  micOff: MicOff,
  refreshCw: RefreshCw,
  shield: Shield,
  badgeCheck: BadgeCheck,
  focus: Focus,
  users: Users,
  clock: Clock,
  checkCircle: CheckCircle,
  fileText: FileText,
  slidersHorizontal: SlidersHorizontal,
  zap: Zap,
  video: Video,
  lightbulb: Lightbulb,
  graduationCap: GraduationCap,
  target: Target,
  youtube: Youtube,
  megaphone: Megaphone,
  sparkles: Sparkles,
  mic2: Mic2Icon,
  audioLines: AudioLines,
  creditCard: CreditCard,
  gift: Gift
};

export default function WhyChooseUsSection({
  badge,
  title,
  description,
  isSecondary,
  id,
  options,
  ctaText
}: WhyChooseUsProps): React.JSX.Element {
  return (
    <section
      className="bg-videomule-white relative"
      id={id}
    >
      <div className="container">
        {/* Header */}
        <SiteHeading
          badge={badge}
          title={title}
          isSecondary={isSecondary}
          description={description}
        />

        {/* Central Element */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="bg-gradient-to-br from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-1.5 shadow-xl">
              <div className="bg-primary rounded-xl px-6 sm:px-8 py-4 sm:py-6 text-center min-w-[180px] sm:min-w-[220px]">
                <Image
                  src="/videomule-logo-white.png"
                  alt="VideoMule"
                  width={120}
                  height={32}
                  className="h-6 sm:h-8 w-auto mx-auto mb-2"
                />
                <span className="text-white/90 font-medium text-sm sm:text-base">
                  Approach
                </span>
              </div>
            </div>
            {/* Vertical line down with animation */}
            <div className="absolute left-1/2 -bottom-12 w-px h-12 -translate-x-1/2 overflow-hidden">
              <div className="w-full h-full bg-gradient-to-b from-primary via-gray-300 to-gray-300 dark:to-gray-600 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Connection Lines SVG - Desktop Row 1 with Traveling Dots */}
        <BlurFade
          inView
          delay={0.1}
          className="hidden lg:block relative h-16"
        >
          <svg
            className="w-full h-full"
            viewBox="0 0 1000 60"
            fill="none"
            preserveAspectRatio="none"
          >
            <defs>
              <filter
                id="dotGlowWhy"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur
                  stdDeviation="3"
                  result="blur"
                />
                <feComposite
                  in="SourceGraphic"
                  in2="blur"
                  operator="over"
                />
              </filter>
              <radialGradient
                id="dotGradientWhy"
                cx="50%"
                cy="50%"
                r="50%"
              >
                <stop
                  offset="0%"
                  stopColor="var(--primary)"
                  stopOpacity="1"
                />
                <stop
                  offset="60%"
                  stopColor="var(--primary)"
                  stopOpacity="0.8"
                />
                <stop
                  offset="100%"
                  stopColor="var(--primary)"
                  stopOpacity="0.4"
                />
              </radialGradient>
            </defs>

            {/* Static lines */}
            <path
              d="M500 0 L500 20 L167 20 L167 60"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 20 L500 60"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 20 L833 20 L833 60"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />

            {/* Traveling dots */}
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 20 L167 20 L167 60"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 60"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  begin="0.5s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 20 L833 20 L833 60"
                />
              </circle>
            </g>
          </svg>
        </BlurFade>

        {/* Process Cards - Row 1 (3 cards) */}
        <div className="grid md:grid-cols-3 gap-7 mb-7 md:mb-0">
          {options?.slice(0, 3).map((item, index) => (
            <BlurFade
              inView
              delay={index * 0.1}
              key={index}
              className="group"
            >
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-5 sm:p-6 border border-slate-200/80 dark:border-slate-700/50 h-full group-hover:shadow-lg group-hover:-translate-y-1 transition-all duration-300 group-hover:border-primary/30">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-sm border border-slate-200/50 dark:border-slate-700/50 group-hover:border-primary/30 transition-colors">
                    {(() => {
                      const Icon = iconMap[item.icon];
                      return Icon ? (
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                      ) : null;
                    })()}
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 text-center">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-[1.7] text-center">
                  {item.description}
                </p>
              </div>
            </BlurFade>
          ))}
        </div>

        {/* Connection Lines SVG - Between Rows */}
        <BlurFade
          inView
          delay={0.2}
          className="hidden lg:block relative h-12"
        >
          <svg
            className="w-full h-full"
            viewBox="0 0 1000 50"
            fill="none"
            preserveAspectRatio="none"
          >
            <path
              d="M167 0 L167 50"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M500 0 L500 50"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M833 0 L833 50"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-gray-300 dark:text-gray-600"
              fill="none"
              strokeLinecap="round"
            />

            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M167 0 L167 50"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin="0.4s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M500 0 L500 50"
                />
              </circle>
            </g>
            <g filter="url(#dotGlowWhy)">
              <circle
                r="5"
                fill="url(#dotGradientWhy)"
              >
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin="0.8s"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1"
                  path="M833 0 L833 50"
                />
              </circle>
            </g>
          </svg>
        </BlurFade>

        {/* Process Cards - Row 2 (3 cards) */}
        <div className="grid md:grid-cols-3 gap-7">
          {options?.slice(3, 6).map((item, index) => (
            <BlurFade
              inView
              delay={index * 0.2}
              key={index}
              className="group"
            >
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-5 sm:p-6 border border-slate-200/80 dark:border-slate-700/50 h-full group-hover:shadow-lg group-hover:-translate-y-1 transition-all duration-300 group-hover:border-primary/30">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-sm border border-slate-200/50 dark:border-slate-700/50 group-hover:border-primary/30 transition-colors">
                    {(() => {
                      const Icon = iconMap[item.icon];
                      return Icon ? (
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                      ) : null;
                    })()}
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 text-center">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-[1.7] text-center">
                  {item.description}
                </p>
              </div>
            </BlurFade>
          ))}
        </div>
        {/* Call to Action */}
        <BlurFade
          inView
          delay={0.3}
        >
          <div className="text-center mt-10 md:mt-12 lg:mt-15">
            <Button
              href={routes.dashboard.auth.SignUp}
              onClick={() => trackCta('cta_signup_clicked', { surface: 'tool_why_choose_us', path: window.location.pathname })}
              size="lg"
              className="text-base lg:text-lg px-10 hover-scale transition-all duration-300 hover:shadow-lg"
            >
              {ctaText}
            </Button>
          </div>
        </BlurFade>
      </div>

      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
    </section>
  );
}
